'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { MapPin, Car, ChevronRight, Star, Clock, Shield, Loader2, Search, CreditCard, CheckCircle, X, Users, Leaf, ArrowRight, Navigation, Lock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface PricingRule {
  id: string;
  category_id: string | null;
  name: string;
  base_fare: number;
  per_km_rate: number;
  minimum_fare: number;
}

interface PassengerInfo {
  full_name: string;
  email: string;
  phone: string;
}

interface AddressSuggestion {
  label: string;
  value: string;
}

type RideType = 'economy' | 'premium' | 'shared';
type Step = 'home' | 'route' | 'ride_type' | 'fare' | 'passenger' | 'payment';

// ─── Ride type config ─────────────────────────────────────────────────────────
const RIDE_TYPES: {
  id: RideType;
  label: string;
  desc: string;
  icon: React.ElementType;
  multiplier: number;
  eta: string;
  seats: number;
  badge?: string;
}[] = [
  {
    id: 'economy',
    label: 'Economy',
    desc: 'Affordable everyday rides',
    icon: Leaf,
    multiplier: 1.0,
    eta: '3–5 min',
    seats: 4,
  },
  {
    id: 'premium',
    label: 'Premium',
    desc: 'Executive vehicles, top-rated drivers',
    icon: Star,
    multiplier: 1.65,
    eta: '5–8 min',
    seats: 4,
    badge: 'Popular',
  },
  {
    id: 'shared',
    label: 'Shared',
    desc: 'Share your ride, split the fare',
    icon: Users,
    multiplier: 0.65,
    eta: '6–10 min',
    seats: 3,
    badge: 'Save 35%',
  },
];

// ─── Address autocomplete suggestions (mock + real-world feel) ────────────────
function buildSuggestions(query: string): AddressSuggestion[] {
  if (!query || query.length < 3) return [];
  const base = [
    'Airport Terminal 1', 'Airport Terminal 2', 'Central Station',
    'City Hall', 'Convention Centre', 'Crown Casino', 'Darling Harbour',
    'Federation Square', 'Grand Hyatt Hotel', 'Hilton Hotel',
    'Main Street', 'Market Square', 'North Bridge', 'Opera House',
    'Parliament House', 'Queen Victoria Market', 'Royal Hospital',
    'Shopping Mall', 'South Wharf', 'Town Hall', 'University Campus',
    'Victoria Gardens', 'Westfield Shopping Centre',
  ];
  return base
    .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 5)
    .map((s) => ({ label: s, value: s }));
}

// ─── AddressInput component ───────────────────────────────────────────────────
function AddressInput({
  label,
  value,
  onChange,
  placeholder,
  dotColor,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  dotColor: string;
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSuggestions(buildSuggestions(value));
    setOpen(value.length >= 3);
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="label-field">{label}</label>
      <div className="relative">
        <div
          className="absolute left-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ background: dotColor }}
        />
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => value.length >= 3 && setOpen(true)}
          className="input-field pl-8 pr-8"
          autoComplete="off"
        />
        {value && (
          <button
            onClick={() => { onChange(''); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl border shadow-lg overflow-hidden"
          style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
        >
          {suggestions.map((s) => (
            <button
              key={s.value}
              onClick={() => { onChange(s.value); setOpen(false); }}
              className="w-full px-4 py-2.5 text-left flex items-center gap-2.5 hover:opacity-80 transition-opacity border-b last:border-b-0 text-sm"
              style={{ borderColor: 'var(--border)' }}
            >
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Mock card form (Stripe Elements placeholder) ─────────────────────────────
function CardForm({
  onReady,
}: {
  onReady: (token: { last4: string; brand: string }) => void;
}) {
  const [cardNum, setCardNum] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);

  const formatCard = (v: string) =>
    v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  const formatExpiry = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };

  const isComplete =
    cardNum.replace(/\s/g, '').length === 16 &&
    expiry.length === 5 &&
    cvc.length >= 3 &&
    name.length >= 2;

  const handleSave = () => {
    if (!isComplete) return;
    const last4 = cardNum.replace(/\s/g, '').slice(-4);
    const brand = cardNum.startsWith('4') ? 'Visa' : cardNum.startsWith('5') ? 'Mastercard' : 'Card';
    setSaved(true);
    onReady({ last4, brand });
  };

  if (saved) {
    const last4 = cardNum.replace(/\s/g, '').slice(-4);
    const brand = cardNum.startsWith('4') ? 'Visa' : cardNum.startsWith('5') ? 'Mastercard' : 'Card';
    return (
      <div
        className="flex items-center gap-3 p-3.5 rounded-xl border"
        style={{ background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.3)' }}
      >
        <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--success)' }} />
        <div className="flex-1">
          <p className="text-sm font-medium">{brand} •••• {last4}</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Card saved for this booking</p>
        </div>
        <button
          onClick={() => { setSaved(false); onReady({ last4: '', brand: '' }); }}
          className="text-xs px-2.5 py-1 rounded-lg"
          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="label-field">Cardholder Name</label>
        <input
          type="text"
          placeholder="John Smith"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input-field"
        />
      </div>
      <div>
        <label className="label-field">Card Number</label>
        <div className="relative">
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="4242 4242 4242 4242"
            value={cardNum}
            onChange={(e) => setCardNum(formatCard(e.target.value))}
            className="input-field pl-9"
            inputMode="numeric"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-field">Expiry</label>
          <input
            type="text"
            placeholder="MM/YY"
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            className="input-field"
            inputMode="numeric"
          />
        </div>
        <div>
          <label className="label-field">CVC</label>
          <input
            type="text"
            placeholder="123"
            value={cvc}
            onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="input-field"
            inputMode="numeric"
          />
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={!isComplete}
        className="btn-primary w-full"
        style={{ opacity: isComplete ? 1 : 0.5 }}
      >
        Save Card
      </button>
      <p className="text-xs text-center flex items-center justify-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
        <Lock className="w-3 h-3" />
        Secured by Stripe — test with 4242 4242 4242 4242
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PassengerApp() {
  const supabase = createClient();
  const [step, setStep] = useState<Step>('home');
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [estimatedDistance, setEstimatedDistance] = useState<number>(5);
  const [selectedRideType, setSelectedRideType] = useState<RideType>('economy');
  const [estimatedFare, setEstimatedFare] = useState<number>(0);
  const [passengerInfo, setPassengerInfo] = useState<PassengerInfo>({ full_name: '', email: '', phone: '' });
  const [savedCard, setSavedCard] = useState<{ last4: string; brand: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState<string>('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);

  // Fetch company + pricing
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: company } = await supabase.from('companies').select('id').limit(1).single();
      const cId = company?.id || '';
      setCompanyId(cId);
      if (cId) {
        const { data: pricing } = await supabase
          .from('pricing_rules')
          .select('*')
          .eq('company_id', cId)
          .eq('is_active', true);
        setPricingRules(pricing || []);
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Recalculate fare when ride type or distance changes
  useEffect(() => {
    const rule = pricingRules[0];
    if (!rule) {
      // Fallback pricing
      const base = { economy: 8, premium: 14, shared: 5 };
      const perKm = { economy: 1.8, premium: 3.2, shared: 1.1 };
      let fare = base[selectedRideType] + perKm[selectedRideType] * estimatedDistance;
      setEstimatedFare(Math.round(fare * 100) / 100);
      return;
    }
    const rt = RIDE_TYPES.find((r) => r.id === selectedRideType)!;
    const raw = (rule.base_fare + rule.per_km_rate * estimatedDistance) * rt.multiplier;
    setEstimatedFare(Math.max(Math.round(raw * 100) / 100, rule.minimum_fare));
  }, [selectedRideType, estimatedDistance, pricingRules]);

  const goBack = () => {
    const prev: Record<Step, Step> = {
      home: 'home', route: 'home', ride_type: 'route',
      fare: 'ride_type', passenger: 'fare', payment: 'passenger',
    };
    setStep(prev[step]);
  };

  const handleProceedToPayment = async () => {
    if (!passengerInfo.full_name || !passengerInfo.email) return;
    setStep('payment');
  };

  const handleConfirmBooking = async () => {
    if (!savedCard) return;
    setCheckoutLoading(true);
    try {
      // Create or find passenger
      let passengerId: string | null = null;
      const { data: existing } = await supabase
        .from('passengers')
        .select('id')
        .eq('company_id', companyId)
        .eq('email', passengerInfo.email)
        .maybeSingle();

      if (existing) {
        passengerId = existing.id;
      } else {
        const { data: newP } = await supabase
          .from('passengers')
          .insert({ company_id: companyId, full_name: passengerInfo.full_name, email: passengerInfo.email, phone: passengerInfo.phone })
          .select('id')
          .single();
        passengerId = newP?.id || null;
      }

      // Create booking
      const { data: booking, error: bookingErr } = await supabase
        .from('bookings')
        .insert({
          company_id: companyId,
          passenger_id: passengerId,
          pickup_address: pickup,
          dropoff_address: dropoff,
          booking_type: selectedRideType,
          status: 'pending',
          payment_status: 'pending',
          estimated_fare: estimatedFare,
          notes: `Ride type: ${selectedRideType} | Card: ${savedCard.brand} ****${savedCard.last4}`,
        })
        .select('id')
        .single();

      if (bookingErr || !booking) throw new Error(bookingErr?.message || 'Failed to create booking');

      // Redirect to Stripe checkout
      const res = await fetch('/api/pwa/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: booking.id,
          companyId,
          passengerId,
          passengerEmail: passengerInfo.email,
          passengerName: passengerInfo.full_name,
          pickupAddress: pickup,
          dropoffAddress: dropoff,
          estimatedFare,
          currency: 'gbp',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading RideOS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-20 px-4 py-3 flex items-center gap-3 border-b"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {step !== 'home' && (
          <button onClick={goBack} className="p-1.5 rounded-lg" style={{ background: 'var(--muted)' }}>
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
        )}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
            <Car className="w-4 h-4" style={{ color: 'var(--primary-foreground)' }} />
          </div>
          <span className="font-bold text-sm">RideOS Passenger</span>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--success)' }} />
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Live</span>
        </div>
      </div>

      {/* Progress bar */}
      {step !== 'home' && (
        <div className="h-0.5 w-full" style={{ background: 'var(--muted)' }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              background: 'var(--primary)',
              width: `${(['route', 'ride_type', 'fare', 'passenger', 'payment'].indexOf(step) + 1) * 20}%`,
            }}
          />
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-6 space-y-5">

        {/* ── HOME ─────────────────────────────────────────────────────────── */}
        {step === 'home' && (
          <>
            <div>
              <h1 className="text-2xl font-bold mb-1">Where to?</h1>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Book a ride in seconds</p>
            </div>

            {/* Quick address entry on home */}
            <div
              className="p-4 rounded-2xl border space-y-3"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <AddressInput
                label="Pickup"
                value={pickup}
                onChange={setPickup}
                placeholder="Enter pickup address"
                dotColor="var(--success)"
              />
              <div className="border-t" style={{ borderColor: 'var(--border)' }} />
              <AddressInput
                label="Drop-off"
                value={dropoff}
                onChange={setDropoff}
                placeholder="Enter destination"
                dotColor="var(--danger)"
              />
            </div>

            <button
              onClick={() => { if (pickup && dropoff) setStep('ride_type'); else setStep('route'); }}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              {pickup && dropoff ? 'Choose Ride Type' : 'Enter Addresses'}
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* Feature pills */}
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { icon: Shield, label: 'Safe Rides', color: 'var(--success)' },
                { icon: Clock, label: 'On Time', color: 'var(--info)' },
                { icon: Star, label: 'Top Rated', color: 'var(--warning)' },
              ].map(({ icon: Icon, label, color }) => (
                <div
                  key={label}
                  className="p-3 rounded-xl text-center border"
                  style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1.5" style={{ color }} />
                  <p className="text-xs font-medium">{label}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── ROUTE ────────────────────────────────────────────────────────── */}
        {step === 'route' && (
          <>
            <div>
              <h2 className="text-xl font-bold mb-1">Your Route</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Enter pickup and drop-off locations</p>
            </div>

            <div
              className="p-4 rounded-2xl border space-y-3"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <AddressInput
                label="Pickup Location"
                value={pickup}
                onChange={setPickup}
                placeholder="e.g. Airport Terminal 1"
                dotColor="var(--success)"
              />
              <div className="flex items-center gap-2 px-1">
                <div className="flex-1 border-t border-dashed" style={{ borderColor: 'var(--border)' }} />
                <Navigation className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />
                <div className="flex-1 border-t border-dashed" style={{ borderColor: 'var(--border)' }} />
              </div>
              <AddressInput
                label="Drop-off Location"
                value={dropoff}
                onChange={setDropoff}
                placeholder="e.g. City Hall"
                dotColor="var(--danger)"
              />
            </div>

            <div>
              <label className="label-field">Estimated Distance (km)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={0.5}
                  value={estimatedDistance}
                  onChange={(e) => setEstimatedDistance(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <span
                  className="text-sm font-bold w-14 text-right"
                  style={{ color: 'var(--primary)' }}
                >
                  {estimatedDistance} km
                </span>
              </div>
            </div>

            <button
              onClick={() => { if (pickup && dropoff) setStep('ride_type'); }}
              disabled={!pickup || !dropoff}
              className="btn-primary w-full"
              style={{ opacity: pickup && dropoff ? 1 : 0.5 }}
            >
              Choose Ride Type
            </button>
          </>
        )}

        {/* ── RIDE TYPE ────────────────────────────────────────────────────── */}
        {step === 'ride_type' && (
          <>
            <div>
              <h2 className="text-xl font-bold mb-1">Choose Ride Type</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                {pickup} → {dropoff}
              </p>
            </div>

            <div className="space-y-3">
              {RIDE_TYPES.map((rt) => {
                const rule = pricingRules[0];
                let fare: number;
                if (rule) {
                  fare = Math.max(
                    (rule.base_fare + rule.per_km_rate * estimatedDistance) * rt.multiplier,
                    rule.minimum_fare
                  );
                } else {
                  const base = { economy: 8, premium: 14, shared: 5 };
                  const perKm = { economy: 1.8, premium: 3.2, shared: 1.1 };
                  fare = base[rt.id] + perKm[rt.id] * estimatedDistance;
                }
                fare = Math.round(fare * 100) / 100;
                const isSelected = selectedRideType === rt.id;

                return (
                  <button
                    key={rt.id}
                    onClick={() => setSelectedRideType(rt.id)}
                    className="w-full p-4 rounded-2xl text-left border transition-all relative overflow-hidden"
                    style={{
                      background: isSelected ? 'rgba(245,158,11,0.08)' : 'var(--card)',
                      borderColor: isSelected ? 'var(--primary)' : 'var(--border)',
                      borderWidth: isSelected ? '2px' : '1px',
                    }}
                  >
                    {rt.badge && (
                      <span
                        className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{
                          background: rt.id === 'shared' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                          color: rt.id === 'shared' ? 'var(--success)' : 'var(--primary)',
                        }}
                      >
                        {rt.badge}
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: isSelected ? 'rgba(245,158,11,0.2)' : 'var(--muted)' }}
                      >
                        <rt.icon
                          className="w-5 h-5"
                          style={{ color: isSelected ? 'var(--primary)' : 'var(--muted-foreground)' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm">{rt.label}</p>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {rt.desc}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                            <Clock className="w-3 h-3" />{rt.eta}
                          </span>
                          <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                            <Users className="w-3 h-3" />{rt.seats} seats
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-lg" style={{ color: 'var(--primary)' }}>
                          £{fare.toFixed(2)}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>est.</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button onClick={() => setStep('fare')} className="btn-primary w-full">
              View Fare Breakdown
            </button>
          </>
        )}

        {/* ── FARE PREVIEW ─────────────────────────────────────────────────── */}
        {step === 'fare' && (
          <>
            <div>
              <h2 className="text-xl font-bold mb-1">Fare Preview</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Review before booking</p>
            </div>

            {/* Big fare card */}
            <div
              className="p-5 rounded-2xl border text-center relative overflow-hidden"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <div
                className="absolute inset-0 opacity-5"
                style={{ background: 'radial-gradient(circle at 50% 0%, var(--primary), transparent 70%)' }}
              />
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  style={{ background: 'rgba(245,158,11,0.12)' }}
                >
                  {(() => {
                    const rt = RIDE_TYPES.find((r) => r.id === selectedRideType)!;
                    return <rt.icon className="w-7 h-7" style={{ color: 'var(--primary)' }} />;
                  })()}
                </div>
                <p className="text-4xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
                  £{estimatedFare.toFixed(2)}
                </p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {RIDE_TYPES.find((r) => r.id === selectedRideType)?.label} · Estimated fare
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div
              className="p-4 rounded-2xl border space-y-2.5"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                Fare Breakdown
              </p>
              {[
                { label: 'Ride Type', value: RIDE_TYPES.find((r) => r.id === selectedRideType)?.label },
                { label: 'Pickup', value: pickup },
                { label: 'Drop-off', value: dropoff },
                { label: 'Distance', value: `~${estimatedDistance} km` },
                { label: 'ETA', value: RIDE_TYPES.find((r) => r.id === selectedRideType)?.eta },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between gap-2 text-sm">
                  <span style={{ color: 'var(--muted-foreground)' }}>{label}</span>
                  <span className="font-medium text-right truncate max-w-[55%]">{value || '—'}</span>
                </div>
              ))}
              <div
                className="flex justify-between gap-2 text-sm pt-2 border-t font-semibold"
                style={{ borderColor: 'var(--border)' }}
              >
                <span>Total</span>
                <span style={{ color: 'var(--primary)' }}>£{estimatedFare.toFixed(2)}</span>
              </div>
            </div>

            <div
              className="p-3 rounded-xl text-xs"
              style={{ background: 'rgba(245,158,11,0.08)', color: 'var(--warning)' }}
            >
              Final fare may vary based on actual distance and traffic conditions.
            </div>

            <button onClick={() => setStep('passenger')} className="btn-primary w-full">
              Continue to Booking
            </button>
          </>
        )}

        {/* ── PASSENGER DETAILS ────────────────────────────────────────────── */}
        {step === 'passenger' && (
          <>
            <div>
              <h2 className="text-xl font-bold mb-1">Your Details</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                We'll send your booking confirmation here
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-field">Full Name *</label>
                <input
                  type="text"
                  placeholder="John Smith"
                  value={passengerInfo.full_name}
                  onChange={(e) => setPassengerInfo((p) => ({ ...p, full_name: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label-field">Email Address *</label>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={passengerInfo.email}
                  onChange={(e) => setPassengerInfo((p) => ({ ...p, email: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label-field">Phone Number</label>
                <input
                  type="tel"
                  placeholder="+44 7700 000000"
                  value={passengerInfo.phone}
                  onChange={(e) => setPassengerInfo((p) => ({ ...p, phone: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            {/* Fare summary */}
            <div
              className="p-3.5 rounded-xl border flex items-center justify-between"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {RIDE_TYPES.find((r) => r.id === selectedRideType)?.label} · {pickup} → {dropoff}
                </p>
                <p className="text-sm font-semibold mt-0.5">Total to pay</p>
              </div>
              <p className="text-xl font-bold" style={{ color: 'var(--primary)' }}>
                £{estimatedFare.toFixed(2)}
              </p>
            </div>

            <button
              onClick={handleProceedToPayment}
              disabled={!passengerInfo.full_name || !passengerInfo.email}
              className="btn-primary w-full"
              style={{ opacity: passengerInfo.full_name && passengerInfo.email ? 1 : 0.5 }}
            >
              Add Payment Method
            </button>
          </>
        )}

        {/* ── PAYMENT ──────────────────────────────────────────────────────── */}
        {step === 'payment' && (
          <>
            <div>
              <h2 className="text-xl font-bold mb-1">Payment Method</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Secure payment powered by Stripe
              </p>
            </div>

            {/* Payment method tabs */}
            <div
              className="flex gap-2 p-1 rounded-xl"
              style={{ background: 'var(--muted)' }}
            >
              {[
                { id: 'card', label: 'Credit / Debit Card', icon: CreditCard },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ background: 'var(--card)', color: 'var(--foreground)' }}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Card form */}
            <div
              className="p-4 rounded-2xl border"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <CardForm onReady={(card) => setSavedCard(card.last4 ? card : null)} />
            </div>

            {/* Order summary */}
            <div
              className="p-4 rounded-2xl border space-y-2"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                Order Summary
              </p>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--muted-foreground)' }}>
                  {RIDE_TYPES.find((r) => r.id === selectedRideType)?.label} ride
                </span>
                <span>£{estimatedFare.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--muted-foreground)' }}>Booking fee</span>
                <span>£0.00</span>
              </div>
              <div
                className="flex justify-between text-sm font-bold pt-2 border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                <span>Total</span>
                <span style={{ color: 'var(--primary)' }}>£{estimatedFare.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={handleConfirmBooking}
              disabled={!savedCard || checkoutLoading}
              className="btn-primary w-full"
              style={{ opacity: savedCard && !checkoutLoading ? 1 : 0.5 }}
            >
              {checkoutLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Lock className="w-4 h-4" />
                  Confirm & Pay £{estimatedFare.toFixed(2)}
                </span>
              )}
            </button>

            <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
              By confirming you agree to our terms. Payment processed securely via Stripe.
            </p>
          </>
        )}

      </div>
    </div>
  );
}
